<template>
  <div
    class="popup-holder popup-delete-account"
    :style="[showDeleteAccountPopup ? '' : 'display:none']"
  >
    <div class="popup-wrap">
      <div class="popup-head">
        <h2>Delete Account</h2>
        <a
          href="#"
          class="popup-close icon-close"
          aria-label="Close"
          @click="closePopup"
        ></a>
      </div>
      <div class="popup-body">
        <div class="warning-message">
          <div class="icon-warning"></div>
          <p><strong>Are you sure you want to delete your account?</strong></p>
        </div>
        <p>If you delete your account:</p>
        <ul class="info-list">
          <li v-if="subscriptionLine">{{ subscriptionLine }}</li>
          <li>All your uploaded media files will be deleted.</li>
          <li>All your transcripts will be deleted.</li>
          <li>All your folders will be deleted.</li>
          <li>Your account will be deleted.</li>
        </ul>

        <!-- App Store / Google Play billing isn't ours to stop: deleting the
             account does NOT cancel it, and the server refuses deletion while
             auto-renew is still on (409). Same gated acknowledgment as iOS.
             While the source is unknown the notice is provider-neutral. -->
        <p
          v-if="checkingSubscription"
          class="store-notice__checking"
          aria-live="polite"
        >
          Checking your subscription…
        </p>
        <div v-else-if="storeNotice" class="store-notice" role="note">
          <p class="store-notice__title">
            <strong>{{ storeNotice.title }}</strong>
          </p>
          <p>{{ storeNotice.body }}</p>
          <p class="store-notice__links">
            <a
              v-for="link in storeNotice.links"
              :key="link.url"
              :href="link.url"
              target="_blank"
              rel="noopener"
              >{{ link.label }}</a
            >
          </p>
          <label class="checkbox-label">
            <input
              type="checkbox"
              v-model="storeAcknowledged"
              style="margin-right: 5px"
            />
            {{ storeNotice.ack }}
          </label>
        </div>
        <p>
          <strong>This takes effect immediately and can't be undone.</strong>
        </p>
        <p>You won't be able to use AudioScribe unless you sign up again.</p>

        <!-- Optional feedback section -->
        <div class="feedback-section">
          <h3>Help us improve (optional)</h3>
          <div class="form-group">
            <label for="deletion-reason"
              >Why are you deleting your account?</label
            >
            <select
              id="deletion-reason"
              v-model="deletionReason"
              class="form-control"
            >
              <option value="">Select a reason...</option>
              <option value="too_expensive">Too expensive</option>
              <option value="not_using">Not using the service</option>
              <option value="missing_features">Missing features I need</option>
              <option value="found_alternative">
                Found a better alternative
              </option>
              <option value="privacy_concerns">Privacy concerns</option>
              <option value="technical_issues">Technical issues</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div class="form-group">
            <label for="deletion-feedback">Any additional feedback?</label>
            <textarea
              id="deletion-feedback"
              v-model="deletionFeedback"
              class="form-control"
              placeholder="Your feedback helps us improve..."
              rows="3"
            ></textarea>
          </div>
        </div>

        <p>Are you sure this is what you want?</p>
        <label class="checkbox-label">
          <input
            type="checkbox"
            v-model="confirmDelete"
            style="margin-right: 5px"
          />
          Yes, I'm sure I want to delete my account. I understand this is final
          and can't be undone.
        </label>
        <div class="btn-list">
          <a href="#" class="btn btn-secondary-outline" @click="closePopup"
            >Cancel</a
          >
          <!-- An anchor ignores `disabled`: aria-disabled tells assistive
               tech, and deleteAccount() enforces it. As a role="button",
               Enter and Space activate it like a click. -->
          <a
            href="#"
            role="button"
            class="btn btn-danger"
            :class="{ disabled: !canDelete || isDeleting }"
            :aria-disabled="String(!canDelete || isDeleting)"
            @click.prevent="deleteAccount"
            @keydown.enter.prevent="deleteAccount"
            @keydown.space.prevent="deleteAccount"
            >{{ isDeleting ? "Deleting..." : "Delete Account" }}</a
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAppStore } from "@/stores/app-store";
import { useWorkSpaceStore } from "@/stores/workspace-store";
import { deleteUserAccount } from "@/services/workspace.service";
import { auth0 } from "@/services/auth0";
import { analytics } from "@/services/analytics.service";
import { mapStores } from "pinia";

const APP_STORE_LINK = {
  label: "Manage App Store subscriptions",
  url: "https://apps.apple.com/account/subscriptions",
};
const GOOGLE_PLAY_LINK = {
  label: "Manage Google Play subscriptions",
  url: "https://play.google.com/store/account/subscriptions",
};

// "both" only says more than one provider grants access, not which ones, and
// an unknown source (status not loaded) could be any of them — so both get
// this provider-neutral notice.
const ANY_STORE_NOTICE = {
  title: "Deleting your account won't cancel App Store or Google Play billing",
  body: "If you subscribed in the App Store or Google Play, turn off auto-renew there first.",
  links: [APP_STORE_LINK, GOOGLE_PLAY_LINK],
  ack: "I've turned off auto-renew for any App Store or Google Play subscription.",
};

// Keyed by the entitlement `source` the API returns (see workspace-store
// subscriptionSource): "stripe" | "apple" | "google_play" | "both" | "comp" | "none".
const STORE_NOTICES = {
  apple: {
    title: "Your App Store subscription won't be canceled",
    body: "Deleting your account doesn't stop App Store billing. Turn off auto-renew first: on your iPhone or iPad, open Settings → your name → Subscriptions → AudioScribe.",
    links: [APP_STORE_LINK],
    ack: "I've turned off auto-renew for my App Store subscription.",
  },
  google_play: {
    title: "Your Google Play subscription won't be canceled",
    body: "Deleting your account doesn't stop Google Play billing. Cancel it first in the Play Store → Payments & subscriptions → Subscriptions.",
    links: [GOOGLE_PLAY_LINK],
    ack: "I've canceled my Google Play subscription.",
  },
  both: ANY_STORE_NOTICE,
};

// The API refuses deletion (409) with one of these codes as the message.
// Copy matches the iOS app so every platform tells the user the same thing.
const BLOCKED_MESSAGES = {
  requires_apple_autorenew_off:
    "Your App Store subscription still has auto-renewal turned on, so Apple would keep billing you after deletion. Turn off auto-renewal first (Settings → your name → Subscriptions → AudioScribe), then delete your account.",
  requires_google_autorenew_off:
    "Your Google Play subscription still renews automatically, so Google would keep billing you after deletion. Cancel it first in the Play Store (Payments & subscriptions → Subscriptions), then delete your account.",
  purchase_in_progress:
    "A purchase on your account is still being processed. Please wait a few minutes and try again.",
};
// A 409 with a code we don't recognize is still a refusal, not a glitch.
const UNKNOWN_CONFLICT =
  "Your account can't be deleted right now. Please try again later or contact support@audioscribe.io.";
const GENERIC_ERROR = "Failed to delete account. Please try again.";

function deletionFailure(error) {
  const isConflict = error?.status === 409;
  const code =
    isConflict && BLOCKED_MESSAGES[error.message] ? error.message : null;
  if (code) return { reason: code, message: BLOCKED_MESSAGES[code] };
  if (isConflict)
    return { reason: "conflict_unknown", message: UNKNOWN_CONFLICT };
  return {
    reason: `http_${error?.status ?? "unknown"}`,
    message: GENERIC_ERROR,
  };
}

export default {
  name: "DeleteAccountPopup",
  props: ["showDeleteAccountPopup"],
  data() {
    return {
      confirmDelete: false,
      storeAcknowledged: false,
      checkingSubscription: false,
      isDeleting: false,
      deletionReason: "",
      deletionFeedback: "",
    };
  },
  computed: {
    ...mapStores(useAppStore, useWorkSpaceStore),
    // null means unknown (no successful status check yet) — never "free".
    subscriptionSource() {
      return this.workspaceStore.subscriptionStatusSucceeded
        ? this.workspaceStore.subscriptionSource
        : null;
    },
    /**
     * Only a web (Stripe) subscription is canceled by deletion — the API
     * cancels it before deleting. Store subscriptions are not, so they get
     * the notice below instead of a promise we can't keep.
     */
    subscriptionLine() {
      if (this.subscriptionSource === "stripe") {
        return "Your subscription will be canceled.";
      }
      if (
        this.subscriptionSource === "both" ||
        this.subscriptionSource === null
      ) {
        return "Your web subscription, if you have one, will be canceled.";
      }
      return null;
    },
    storeNotice() {
      if (this.subscriptionSource === null) return ANY_STORE_NOTICE;
      return STORE_NOTICES[this.subscriptionSource] || null;
    },
    canDelete() {
      return (
        this.confirmDelete &&
        !this.checkingSubscription &&
        (!this.storeNotice || this.storeAcknowledged)
      );
    },
  },
  watch: {
    showDeleteAccountPopup: {
      immediate: true,
      handler(open) {
        if (open) this.ensureSubscriptionStatus();
      },
    },
  },
  methods: {
    // Which notice applies depends on where the user subscribed, so check it
    // fresh on every open and trust only this check (a tab can be days old,
    // and the store resets its success flag when a check starts). Deletion
    // stays blocked while it runs; if it fails, the neutral notice (with its
    // required acknowledgment) stands in rather than a stale answer.
    async ensureSubscriptionStatus() {
      if (this.checkingSubscription) return;
      this.checkingSubscription = true;
      try {
        await this.workspaceStore.fetchSubscriptionStatus();
      } catch (error) {
        console.error("Subscription check before deletion failed:", error);
      } finally {
        this.checkingSubscription = false;
      }
    },
    closePopup() {
      this.appStore.showDeleteAccountPopup = false;
      this.confirmDelete = false;
      this.storeAcknowledged = false;
      this.deletionReason = "";
      this.deletionFeedback = "";
      this.isDeleting = false;
    },
    async deleteAccount() {
      if (!this.canDelete || this.isDeleting) return;

      this.isDeleting = true;

      let result;
      try {
        // The API helper reports HTTP failures as { error } — it does NOT
        // throw. Ignoring that used to announce "deleted" and sign the user
        // out while the account (and any store billing) was still live.
        result =
          (await deleteUserAccount(
            this.deletionReason,
            this.deletionFeedback,
          )) || {};
      } catch (error) {
        console.error("Failed to delete account:", error);
        result = { error: {} };
      }
      if (result.error) {
        this.handleDeletionError(result.error);
        return;
      }
      await this.signOutDeletedAccount();
    },
    // The account is already gone on the server, so nothing from here on may
    // report the deletion as failed.
    async signOutDeletedAccount() {
      this.$toast.success("Account deleted successfully");
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (error) {
        console.error("Clearing storage after account deletion failed:", error);
      }
      try {
        await auth0.logout({
          logoutParams: {
            returnTo: globalThis.location.origin,
          },
        });
      } catch (error) {
        console.error("Sign-out after account deletion failed:", error);
        // Leave the app regardless: this session belongs to a deleted account.
        globalThis.location.replace(globalThis.location.origin);
      }
    },
    handleDeletionError(error) {
      const { reason, message } = deletionFailure(error);
      analytics.accountDeletionFailed({ reason });
      this.$toast.error(message);
      this.isDeleting = false;
    },
  },
};
</script>

<style scoped>
.popup-delete-account .store-notice {
  border: 1px solid #f5c6cb;
  background: #fff5f5;
  border-radius: 6px;
  padding: 12px;
  margin: 0 0 15px 0;
}

.popup-delete-account .store-notice__checking {
  margin: 0 0 15px 0;
  color: #6c757d;
}

.popup-delete-account .store-notice p {
  margin: 0 0 8px 0;
}

.popup-delete-account .store-notice__links a {
  display: inline-block;
  margin-right: 12px;
  text-decoration: underline;
}

.popup-delete-account .store-notice .checkbox-label {
  margin: 8px 0 0 0;
}

.popup-delete-account .warning-message {
  display: flex;
  align-items: center;
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 15px;
}

.popup-delete-account .warning-message .icon-warning {
  background: url("/path-to-your-warning-icon.svg") no-repeat center;
  width: 24px;
  height: 24px;
  margin-right: 10px;
}

.popup-delete-account .info-list {
  list-style-type: none;
  padding-left: 0;
  margin: 0 0 15px 0;
}

.popup-delete-account .info-list li {
  margin-bottom: 10px;
}

.popup-delete-account .checkbox-label {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.popup-delete-account .btn-list {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.popup-delete-account .btn-secondary-outline {
  background-color: transparent;
  color: #007bff;
  border: 1px solid #007bff;
  padding: 5px 15px;
  text-decoration: none;
  border-radius: 5px;
}

.popup-delete-account .btn-secondary-outline:hover {
  background-color: #e2e6ea;
  text-decoration: none;
}

.popup-delete-account .btn-danger {
  background-color: #dc3545;
  color: white;
  padding: 5px 15px;
  border: none;
  border-radius: 5px;
  text-decoration: none;
}

.popup-delete-account .btn-danger.disabled {
  background-color: #f8d7da;
  color: #721c24;
  cursor: not-allowed;
}

.popup-delete-account .btn-danger.disabled:hover {
  text-decoration: none;
}

.popup-delete-account .btn-danger:hover:not(.disabled) {
  background-color: #c82333;
  text-decoration: none;
}

.popup-close {
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  position: absolute;
  right: 10px;
  top: 10px;
  text-decoration: none;
}

.popup-close:hover {
  color: #000;
}

.popup-delete-account .feedback-section {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 5px;
  padding: 15px;
  margin: 20px 0;
}

.popup-delete-account .feedback-section h3 {
  font-size: 1.1rem;
  margin-bottom: 15px;
  color: #495057;
}

.popup-delete-account .form-group {
  margin-bottom: 15px;
}

.popup-delete-account .form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #495057;
}

.popup-delete-account .form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.15s ease-in-out;
}

.popup-delete-account .form-control:focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.popup-delete-account textarea.form-control {
  resize: vertical;
  min-height: 60px;
}
</style>
